import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/card/card.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/button-group/button-group.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/checkbox/checkbox.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/select/select.js';
import '@shoelace-style/shoelace/dist/components/option/option.js';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';
import { styles as sharedStyles } from '../styles/shared-styles';
import { money } from '../datatypes/currency';

import '../components/data-table';
import {
  createArrayDataProvider,
  type DataTableCellCommitDetail,
  type DataTableColumnDef,
  type DataTableEditMode,
  type DataTableRowAddDetail,
  type DataTableRowCommitDetail,
} from '../components/data-table';

/*
  Lab page for exercising <app-data-table> in isolation.

  Goal: a tester (or me) can switch the same dataset between every edit
  mode, every editor type, every commit-pipeline knob, and watch a live
  event log to confirm what's actually firing. If the table can't be
  exercised cleanly here, it's not ready for a real page.

  Three independent tables on the page:

    1. Stock-take table — biggest surface. Cycles through the four edit
       modes via the toggle bar. Same column set, same data. Has an
       add-row factory and an async cell-commit handler so you can see
       waitFor / preventNav / deferred nav.

    2. Editor types — a single row with one of each editor (text, number,
       select, checkbox, read-only) in `editMode="table"`. Verifies the
       borderless Shoelace integration looks right side-by-side.

    3. Read-only browse — sortable / hover / selection. Sanity-check the
       theme rewrite in a non-editing context.

  Side panel: validation knobs + an event log that prints every commit /
  add-row event with timestamps, so timing bugs are visible.
*/

interface StockRow {
  id: string;
  sku: string;
  name: string;
  category: 'beans' | 'milk' | 'syrup' | 'pastry' | 'other';
  unit: string;
  expected: number;
  counted: number | null;
  notes: string;
  available: boolean;
}

interface ShowcaseRow {
  id: string;
  text: string;
  num: number;
  pick: 'a' | 'b' | 'c';
  flag: boolean;
  readonly: string;
}

interface ProductRow {
  id: string;
  name: string;
  category: string;
  price: number;
  stock: number;
}

interface LogEntry {
  ts: string;
  channel: 'cell' | 'row' | 'add' | 'select';
  message: string;
  ok: boolean;
}

const CATEGORIES: ReadonlyArray<{
  value: StockRow['category'];
  label: string;
}> = [
  { value: 'beans', label: 'Beans' },
  { value: 'milk', label: 'Milk' },
  { value: 'syrup', label: 'Syrup' },
  { value: 'pastry', label: 'Pastry' },
  { value: 'other', label: 'Other' },
];

function seedStock(): StockRow[] {
  return [
    {
      id: 's1',
      sku: 'B-001',
      name: 'Arabica beans',
      category: 'beans',
      unit: 'kg',
      expected: 12,
      counted: null,
      notes: '',
      available: true,
    },
    {
      id: 's2',
      sku: 'B-002',
      name: 'Robusta beans',
      category: 'beans',
      unit: 'kg',
      expected: 4,
      counted: null,
      notes: '',
      available: true,
    },
    {
      id: 's3',
      sku: 'M-001',
      name: 'Whole milk',
      category: 'milk',
      unit: 'L',
      expected: 18,
      counted: null,
      notes: '',
      available: true,
    },
    {
      id: 's4',
      sku: 'M-002',
      name: 'Oat milk',
      category: 'milk',
      unit: 'L',
      expected: 9,
      counted: null,
      notes: '',
      available: true,
    },
    {
      id: 's5',
      sku: 'S-001',
      name: 'Vanilla syrup',
      category: 'syrup',
      unit: 'btl',
      expected: 6,
      counted: null,
      notes: '',
      available: true,
    },
    {
      id: 's6',
      sku: 'S-002',
      name: 'Caramel syrup',
      category: 'syrup',
      unit: 'btl',
      expected: 6,
      counted: null,
      notes: '',
      available: true,
    },
    {
      id: 's7',
      sku: 'P-001',
      name: 'Croissant',
      category: 'pastry',
      unit: 'pc',
      expected: 24,
      counted: null,
      notes: '',
      available: true,
    },
    {
      id: 's8',
      sku: 'P-002',
      name: 'Pain au chocolat',
      category: 'pastry',
      unit: 'pc',
      expected: 18,
      counted: null,
      notes: '',
      available: false,
    },
  ];
}

function seedShowcase(): ShowcaseRow[] {
  return [
    {
      id: 'sc1',
      text: 'first',
      num: 10,
      pick: 'a',
      flag: true,
      readonly: 'frozen',
    },
    {
      id: 'sc2',
      text: 'second',
      num: 25,
      pick: 'b',
      flag: false,
      readonly: 'frozen',
    },
    {
      id: 'sc3',
      text: 'third',
      num: 0,
      pick: 'c',
      flag: true,
      readonly: 'frozen',
    },
  ];
}

function seedProducts(): ProductRow[] {
  const cats = ['Coffee', 'Tea', 'Pastry', 'Cold drinks', 'Snacks'];
  const out: ProductRow[] = [];
  for (let i = 0; i < 24; i++) {
    out.push({
      id: `p${i + 1}`,
      name: `Product ${i + 1}`,
      category: cats[i % cats.length],
      price: 50 + (i % 7) * 15,
      stock: 100 - (i % 11) * 7,
    });
  }
  return out;
}

@customElement('data-table-lab-page')
export class DataTableLabPage extends LitElement {
  // -------- Stock-take state --------
  @state() private _stock: StockRow[] = seedStock();
  @state() private _stockMode: DataTableEditMode = 'table';

  // -------- Showcase state --------
  @state() private _showcase: ShowcaseRow[] = seedShowcase();

  // -------- Read-only browse state --------
  @state() private _products: ProductRow[] = seedProducts();
  @state() private _productSelected: unknown[] = [];

  // -------- Knobs --------
  @state() private _delayMs = 300;
  @state() private _rejectNegative = true;
  @state() private _failRandom = false;
  @state() private _preventNavOnSpecial = false; // refuses commits that contain "nope"

  // -------- Event log --------
  @state() private _log: LogEntry[] = [];

  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
        max-width: 1400px;
        margin: 16px auto;
        padding: 0 12px 80px;
      }

      h1 {
        font-size: 1.4rem;
        margin: 16px 0 4px;
      }
      h2 {
        margin: 24px 0 8px;
        font-size: 1.05rem;
      }
      .lede {
        opacity: 0.7;
        font-size: 0.9rem;
        max-width: 720px;
      }

      .layout {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 320px;
        gap: 16px;
        align-items: start;
      }

      @media (max-width: 1100px) {
        .layout {
          grid-template-columns: 1fr;
        }
      }

      sl-card.section {
        --padding: 14px;
        display: block;
        width: 100%;
      }

      sl-card.section[muted] {
        --sl-color-neutral-0: var(--sl-color-neutral-50);
      }

      .toolbar {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 10px;
        margin: 8px 0 12px;
      }

      .toolbar-label {
        font-size: 0.78rem;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        opacity: 0.7;
      }

      .toolbar code {
        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
        font-size: 0.78rem;
        background: var(--sl-color-neutral-100);
        padding: 2px 6px;
        border-radius: 6px;
      }

      app-data-table {
        --data-table-radius: var(--sl-border-radius-medium);
      }

      .knobs {
        position: sticky;
        top: 70px;
      }

      .knob-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        padding: 6px 0;
        font-size: 0.85rem;
        border-bottom: 1px dashed var(--sl-color-neutral-200);
      }

      .knob-row:last-of-type {
        border-bottom: none;
      }

      .log {
        max-height: 360px;
        overflow-y: auto;
        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
        font-size: 0.74rem;
        background: var(--sl-color-neutral-100);
        border-radius: var(--sl-border-radius-medium);
        padding: 8px 10px;
        line-height: 1.45;
      }

      @media (prefers-color-scheme: dark) {
        .log {
          background: rgba(255, 255, 255, 0.04);
        }
      }

      .log-entry {
        display: block;
        padding: 1px 0;
        border-bottom: 1px dashed rgba(0, 0, 0, 0.05);
        white-space: pre-wrap;
        word-break: break-word;
      }
      .log-entry:last-child {
        border-bottom: none;
      }
      .log-entry.bad {
        color: var(--sl-color-danger-600);
      }
      .log-entry.ok {
        color: var(--sl-color-success-700);
      }
      .log-empty {
        opacity: 0.6;
        font-style: italic;
      }

      .row-actions {
        display: flex;
        gap: 6px;
        margin-top: 8px;
      }

      .selection-pill {
        font-size: 0.78rem;
        opacity: 0.7;
      }

      .legend {
        font-size: 0.78rem;
        opacity: 0.75;
        margin-top: 6px;
      }
    `,
  ];

  // ────────────────────────────────────────────────────────────
  // Common helpers
  // ────────────────────────────────────────────────────────────

  private _push(channel: LogEntry['channel'], message: string, ok = true) {
    const ts = new Date().toLocaleTimeString(undefined, {
      hour12: false,
    });
    this._log = [{ ts, channel, message, ok }, ...this._log.slice(0, 99)];
  }

  private async _simulateSave(label: string): Promise<void> {
    if (this._delayMs > 0) {
      await new Promise((r) => setTimeout(r, this._delayMs));
    }
    if (this._failRandom && Math.random() < 0.3) {
      throw new Error(`${label}: simulated random save failure`);
    }
  }

  // ────────────────────────────────────────────────────────────
  // Stock-take table
  // ────────────────────────────────────────────────────────────

  private get _stockProvider() {
    return createArrayDataProvider<StockRow>(() => this._stock, {
      filterFn: (item, text) =>
        item.name.toLowerCase().includes(text) ||
        item.sku.toLowerCase().includes(text),
    });
  }

  private _stockColumns(): DataTableColumnDef[] {
    return [
      {
        title: 'SKU',
        fieldName: 'sku',
        sortable: true,
        width: '120px',
      },
      {
        title: 'Name',
        fieldName: 'name',
        sortable: true,
        editable: true,
        editor: (_t, item: StockRow) => {
          return html`<sl-input
            size="small"
            .value=${item.name}
            @sl-change=${(e: CustomEvent) =>
              this._patchStock(item.id, {
                name: (e.target as HTMLInputElement).value,
              })}
          ></sl-input>`;
        },
      },
      {
        title: 'Category',
        fieldName: 'category',
        sortable: true,
        editable: true,
        editor: (_t, item: StockRow) => html`
          <sl-select
            size="small"
            .value=${item.category}
            hoist
            @sl-change=${(e: Event) =>
              this._patchStock(item.id, {
                category: (e.target as HTMLElement & { value: string })
                  .value as StockRow['category'],
              })}
          >
            ${CATEGORIES.map(
              (c) => html`<sl-option value=${c.value}>${c.label}</sl-option>`
            )}
          </sl-select>
        `,
        displayValue: (_t, item: StockRow) =>
          CATEGORIES.find((c) => c.value === item.category)?.label ??
          item.category,
        width: '160px',
      },
      {
        title: 'Unit',
        fieldName: 'unit',
        width: '70px',
        align: 'center',
      },
      {
        title: 'Expected',
        fieldName: 'expected',
        align: 'right',
        width: '100px',
      },
      {
        title: 'Counted',
        fieldName: 'counted',
        align: 'right',
        width: '120px',
        editable: true,
        editor: (_t, item: StockRow) => {
          return html`<sl-input
            type="number"
            size="small"
            .value=${item.counted === null ? '' : String(item.counted)}
            @sl-change=${(e: CustomEvent) => {
              const raw = (e.target as HTMLInputElement).value;
              const parsed = raw === '' ? null : Number(raw);
              this._patchStock(item.id, { counted: parsed });
            }}
          ></sl-input>`;
        },
        displayValue: (_t, item: StockRow) =>
          item.counted === null ? '—' : String(item.counted),
      },
      {
        title: 'Available',
        fieldName: 'available',
        align: 'center',
        width: '100px',
        editable: true,
        editor: (_t, item: StockRow) => html`
          <sl-checkbox
            ?checked=${item.available}
            @sl-change=${(e: CustomEvent) =>
              this._patchStock(item.id, {
                available: (e.target as HTMLInputElement).checked,
              })}
          ></sl-checkbox>
        `,
        displayValue: (_t, item: StockRow) => (item.available ? 'Yes' : 'No'),
      },
      {
        title: 'Notes',
        fieldName: 'notes',
        editable: true,
        editor: (_t, item: StockRow) => html`
          <sl-input
            size="small"
            .value=${item.notes}
            placeholder="(reasonable cause, etc.)"
            @sl-change=${(e: CustomEvent) =>
              this._patchStock(item.id, {
                notes: (e.target as HTMLInputElement).value,
              })}
          ></sl-input>
        `,
      },
    ];
  }

  private _patchStock(id: string, patch: Partial<StockRow>) {
    this._stock = this._stock.map((r) =>
      r.id === id ? { ...r, ...patch } : r
    );
  }

  private _stockKey = (item: StockRow) => item.id;

  private _stockAddRowFactory = (): StockRow => {
    const seq = this._stock.length + 1;
    return {
      id: `new-${Date.now()}-${seq}`,
      sku: `NEW-${String(seq).padStart(3, '0')}`,
      name: '',
      category: 'other',
      unit: 'pc',
      expected: 0,
      counted: null,
      notes: '',
      available: true,
    };
  };

  // ────────────────────────────────────────────────────────────
  // Commit handlers (shared by all stock-table modes)
  // ────────────────────────────────────────────────────────────

  private _onStockCellCommit(e: CustomEvent<DataTableCellCommitDetail>) {
    const { item, fieldName, waitFor, preventNav } = e.detail;
    const row = item as StockRow;

    // Synchronous validations first — these short-circuit before persistence.
    if (
      this._rejectNegative &&
      fieldName === 'counted' &&
      row.counted !== null &&
      row.counted < 0
    ) {
      preventNav();
      this._push('cell', `reject ${row.sku}.counted = ${row.counted}`, false);
      return;
    }

    if (
      this._preventNavOnSpecial &&
      typeof row[fieldName as keyof StockRow] === 'string' &&
      String(row[fieldName as keyof StockRow])
        .toLowerCase()
        .includes('nope')
    ) {
      preventNav();
      this._push(
        'cell',
        `preventNav ${row.sku}.${fieldName} contains "nope"`,
        false
      );
      return;
    }

    waitFor(
      this._simulateSave(`cell ${row.sku}.${fieldName}`)
        .then(() =>
          this._push(
            'cell',
            `committed ${row.sku}.${fieldName} = ${JSON.stringify(
              row[fieldName as keyof StockRow]
            )}`,
            true
          )
        )
        .catch((err: Error) => {
          this._push(
            'cell',
            `FAIL ${row.sku}.${fieldName}: ${err.message}`,
            false
          );
          throw err; // table aborts nav
        })
    );
  }

  private _onStockRowCommit(e: CustomEvent<DataTableRowCommitDetail>) {
    const row = e.detail.item as StockRow;
    e.detail.waitFor(
      this._simulateSave(`row ${row.sku}`).then(() =>
        this._push(
          'row',
          `row-commit ${row.sku} (counted=${row.counted})`,
          true
        )
      )
    );
  }

  private _onStockRowAdd(e: CustomEvent<DataTableRowAddDetail>) {
    const row = e.detail.newItem as StockRow;
    this._stock = [...this._stock, row];
    this._push(
      'add',
      `row-add → ${row.sku} at index ${e.detail.insertIndex}`,
      true
    );
  }

  private _resetStock() {
    this._stock = seedStock();
    this._push('add', 'reset to seed data', true);
  }

  // ────────────────────────────────────────────────────────────
  // Editor-types showcase
  // ────────────────────────────────────────────────────────────

  private get _showcaseProvider() {
    return createArrayDataProvider<ShowcaseRow>(() => this._showcase);
  }

  private _showcaseColumns(): DataTableColumnDef[] {
    return [
      {
        title: 'Text',
        fieldName: 'text',
        editable: true,
        editor: (_t, item: ShowcaseRow) => html`
          <sl-input
            size="small"
            .value=${item.text}
            @sl-change=${(e: CustomEvent) =>
              this._patchShowcase(item.id, {
                text: (e.target as HTMLInputElement).value,
              })}
          ></sl-input>
        `,
      },
      {
        title: 'Number',
        fieldName: 'num',
        align: 'right',
        editable: true,
        editor: (_t, item: ShowcaseRow) => html`
          <sl-input
            type="number"
            size="small"
            .value=${String(item.num)}
            @sl-change=${(e: CustomEvent) =>
              this._patchShowcase(item.id, {
                num: Number((e.target as HTMLInputElement).value),
              })}
          ></sl-input>
        `,
      },
      {
        title: 'Select',
        fieldName: 'pick',
        editable: true,
        editor: (_t, item: ShowcaseRow) => html`
          <sl-select
            size="small"
            hoist
            .value=${item.pick}
            @sl-change=${(e: Event) =>
              this._patchShowcase(item.id, {
                pick: (e.target as HTMLElement & { value: string })
                  .value as ShowcaseRow['pick'],
              })}
          >
            <sl-option value="a">Option A</sl-option>
            <sl-option value="b">Option B</sl-option>
            <sl-option value="c">Option C</sl-option>
          </sl-select>
        `,
        displayValue: (_t, item: ShowcaseRow) =>
          `Option ${item.pick.toUpperCase()}`,
        width: '160px',
      },
      {
        title: 'Checkbox',
        fieldName: 'flag',
        align: 'center',
        editable: true,
        editor: (_t, item: ShowcaseRow) => html`
          <sl-checkbox
            ?checked=${item.flag}
            @sl-change=${(e: CustomEvent) =>
              this._patchShowcase(item.id, {
                flag: (e.target as HTMLInputElement).checked,
              })}
          ></sl-checkbox>
        `,
        displayValue: (_t, item: ShowcaseRow) => (item.flag ? '✓' : '·'),
        width: '110px',
      },
      {
        title: 'Read-only',
        fieldName: 'readonly',
        width: '160px',
      },
    ];
  }

  private _patchShowcase(id: string, patch: Partial<ShowcaseRow>) {
    this._showcase = this._showcase.map((r) =>
      r.id === id ? { ...r, ...patch } : r
    );
  }

  private _showcaseKey = (item: ShowcaseRow) => item.id;

  // ────────────────────────────────────────────────────────────
  // Read-only browse
  // ────────────────────────────────────────────────────────────

  private get _productProvider() {
    return createArrayDataProvider<ProductRow>(() => this._products, {
      filterFn: (item, text) =>
        item.name.toLowerCase().includes(text) ||
        item.category.toLowerCase().includes(text),
    });
  }

  private _productColumns(): DataTableColumnDef[] {
    return [
      { title: 'Name', fieldName: 'name', sortable: true },
      {
        title: 'Category',
        fieldName: 'category',
        sortable: true,
        width: '160px',
      },
      {
        title: 'Price',
        fieldName: 'price',
        align: 'right',
        sortable: true,
        width: '120px',
        displayValue: (_t, item: ProductRow) => `${money(item.price)}`,
      },
      {
        title: 'Stock',
        fieldName: 'stock',
        align: 'right',
        sortable: true,
        width: '110px',
      },
    ];
  }

  private _productKey = (item: ProductRow) => item.id;

  private _onProductSelect(e: CustomEvent) {
    this._productSelected = (e.detail.selectedkeys ?? []) as unknown[];
    this._push(
      'select',
      `select → [${this._productSelected.join(', ')}]`,
      true
    );
  }

  // ────────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────────

  render() {
    return html`
      <h1>Data table — lab</h1>
      <p class="lede">
        Live exercise rig for <code>&lt;app-data-table&gt;</code>. Switch modes,
        edit, watch the event log on the right, and prove the table does what it
        should before wiring it into a real page. Re-seeding does
        <strong>not</strong> rebuild the component instances — only the data.
      </p>

      <div class="layout">
        <div>
          <sl-card class="section">
            <h2 slot="header">
              Stock-take —
              <sl-badge variant="primary" pill>${this._stockMode}</sl-badge>
            </h2>

            <div class="toolbar">
              <span class="toolbar-label">Mode</span>
              <sl-button-group label="Edit mode">
                ${(['none', 'cell', 'row', 'table'] as DataTableEditMode[]).map(
                  (m) => html`
                    <sl-button
                      size="small"
                      variant=${this._stockMode === m ? 'primary' : 'default'}
                      @click=${() => (this._stockMode = m)}
                    >
                      ${m}
                    </sl-button>
                  `
                )}
              </sl-button-group>

              <sl-divider vertical></sl-divider>

              <sl-button size="small" @click=${this._resetStock}>
                Reset data
              </sl-button>

              <span class="toolbar-label" style="margin-left: auto;">
                rows: ${this._stock.length}
              </span>
            </div>

            <div class="legend">
              <code>none</code> = read-only · <code>cell</code> = click a cell
              to edit · <code>row</code> = single-select a row to edit ·
              <code>table</code> = every editable cell is live (stock-take). In
              <code>row</code> mode click a row first; in <code>cell</code> mode
              click an editable cell.
            </div>

            <div style="margin-top: 12px;">
              <app-data-table
                .columns=${this._stockColumns()}
                .dataProvider=${this._stockProvider}
                .keyevent=${this._stockKey}
                .editMode=${this._stockMode}
                .addRowFactory=${this._stockAddRowFactory}
                selectmode=${this._stockMode === 'row' ? 'single' : 'none'}
                tablestyle="halftable"
                @cell-commit=${(e: CustomEvent<DataTableCellCommitDetail>) =>
                  this._onStockCellCommit(e)}
                @row-commit=${(e: CustomEvent<DataTableRowCommitDetail>) =>
                  this._onStockRowCommit(e)}
                @row-add=${(e: CustomEvent<DataTableRowAddDetail>) =>
                  this._onStockRowAdd(e)}
              ></app-data-table>
            </div>
          </sl-card>

          <sl-card class="section" style="margin-top: 16px;">
            <h2 slot="header">Editor types showcase</h2>
            <div class="legend">
              All five editor types in <code>editMode="table"</code>. Use this
              to verify each Shoelace control sits flush in the cell with no
              border bleed.
            </div>
            <div style="margin-top: 12px;">
              <app-data-table
                .columns=${this._showcaseColumns()}
                .dataProvider=${this._showcaseProvider}
                .keyevent=${this._showcaseKey}
                .editMode=${'table' as DataTableEditMode}
                tablestyle="halftable"
                @cell-commit=${(e: CustomEvent<DataTableCellCommitDetail>) => {
                  const row = e.detail.item as ShowcaseRow;
                  e.detail.waitFor(
                    this._simulateSave(
                      `showcase ${row.id}.${e.detail.fieldName}`
                    ).then(() =>
                      this._push(
                        'cell',
                        `showcase ${row.id}.${e.detail.fieldName} = ${JSON.stringify(
                          row[e.detail.fieldName as keyof ShowcaseRow]
                        )}`,
                        true
                      )
                    )
                  );
                }}
              ></app-data-table>
            </div>
          </sl-card>

          <sl-card class="section" style="margin-top: 16px;">
            <h2 slot="header">
              Read-only browse
              ${
                this._productSelected.length > 0
                  ? html`<sl-badge variant="primary" pill
                      >${this._productSelected.length} selected</sl-badge
                    >`
                  : ''
              }
            </h2>
            <div class="legend">
              No editors — sortable headers, hover and single-select rows,
              paginated. Confirms the theme rewrite reads cleanly without edit
              decorations.
            </div>
            <div style="margin-top: 12px;">
              <app-data-table
                .columns=${this._productColumns()}
                .dataProvider=${this._productProvider}
                .keyevent=${this._productKey}
                selectmode="single"
                pageLength=${10}
                tablestyle="halftable"
                @rowselect=${(e: CustomEvent) => this._onProductSelect(e)}
              ></app-data-table>
            </div>
          </sl-card>
        </div>

        <div class="knobs">
          <sl-card class="section" muted>
            <h2 slot="header">Validation knobs</h2>

            <div class="knob-row">
              <span>Reject negative <code>counted</code></span>
              <sl-checkbox
                ?checked=${this._rejectNegative}
                @sl-change=${(e: CustomEvent) =>
                  (this._rejectNegative = (
                    e.target as HTMLInputElement
                  ).checked)}
              ></sl-checkbox>
            </div>

            <div class="knob-row">
              <span><code>preventNav</code> on text containing "nope"</span>
              <sl-checkbox
                ?checked=${this._preventNavOnSpecial}
                @sl-change=${(e: CustomEvent) =>
                  (this._preventNavOnSpecial = (
                    e.target as HTMLInputElement
                  ).checked)}
              ></sl-checkbox>
            </div>

            <div class="knob-row">
              <span>Random 30% save failure</span>
              <sl-checkbox
                ?checked=${this._failRandom}
                @sl-change=${(e: CustomEvent) =>
                  (this._failRandom = (e.target as HTMLInputElement).checked)}
              ></sl-checkbox>
            </div>

            <div class="knob-row">
              <span>Save delay (ms)</span>
              <sl-input
                size="small"
                type="number"
                style="width: 90px;"
                min="0"
                max="5000"
                .value=${String(this._delayMs)}
                @sl-change=${(e: CustomEvent) =>
                  (this._delayMs =
                    Number((e.target as HTMLInputElement).value) || 0)}
              ></sl-input>
            </div>
          </sl-card>

          <sl-card class="section" style="margin-top: 12px;">
            <h2 slot="header">
              Event log
              <sl-icon-button
                name="trash"
                label="Clear log"
                style="margin-left: auto;"
                @click=${() => (this._log = [])}
              ></sl-icon-button>
            </h2>
            <div class="log">
              ${
                this._log.length === 0
                  ? html`<span class="log-empty">No events yet.</span>`
                  : this._log.map(
                      (entry) => html`
                        <span class="log-entry ${entry.ok ? 'ok' : 'bad'}">
                          ${entry.ts} · ${entry.channel} · ${entry.message}
                        </span>
                      `
                    )
              }
            </div>
          </sl-card>

          <sl-card class="section" style="margin-top: 12px;">
            <h2 slot="header">What to try</h2>
            <ol
              style="font-size: 0.83rem; line-height: 1.45; padding-left: 18px; margin: 0;"
            >
              <li>
                Switch the stock-take mode through <code>none</code> →
                <code>cell</code> → <code>row</code> → <code>table</code>.
                Confirm only the right cells render editors per mode.
              </li>
              <li>
                <strong>Tab nav:</strong> in <code>table</code> mode, focus a
                Counted cell and tab — should walk to Available, Notes, then
                wrap to next row's Name.
              </li>
              <li>
                <strong>Vertical nav:</strong> in any edit mode, ArrowDown /
                Enter from a cell should commit and move down. Past the last row
                should trigger add-row.
              </li>
              <li>
                <strong>preventNav:</strong> turn on "Reject negative", type a
                negative number into Counted, Tab. Focus should stay on the bad
                cell. Log should show the rejection.
              </li>
              <li>
                <strong>waitFor race:</strong> set Save delay to 1500ms, edit
                two adjacent cells quickly. Each commit must finish before the
                next runs. Log timestamps prove it.
              </li>
              <li>
                <strong>Add row:</strong> press the "+ Add row" strip; new row
                appears with first editable cell auto-focused. Tab past the last
                cell of the last row also triggers it.
              </li>
              <li>
                <strong>Click-out commit:</strong> in <code>row</code> mode,
                edit a cell then click anywhere outside the table — a
                <code>row</code> event should fire once.
              </li>
            </ol>
          </sl-card>
        </div>
      </div>
    `;
  }
}
